import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";

import { dndSensors, SnapVerticalCenterToCursor } from "@skilitics/designer-ui-components";
import { DragDropProvider, DragOverlay, type DragDropEventHandlers } from "@skilitics/drag-drop";

import { assert } from "../../state";
import { GROUP_CONDITION_CHILDREN_KEY, TYPE_BUILDABLE } from "../constants";
import type { DragEndAddConditionEvent, DragEndAddEffectEvent } from "../dnd/dndEventTypes";
import { draggableDataSchema } from "../dnd/dndSchemas";
import { useOnDragEnd } from "../dnd/useOnDragEnd";
import { useResponseDesigner } from "../ResponseByIdContext";
import { ResponseEditor } from "./ResponseEditor";
import { ResponseEditorContextProvider } from "./ResponseEditorContextProvider";
import { DragOverlayDragItem } from "./ResponseEditorDragOverlayItem";

type Props = {
  responseId: number;
};

export const ResponseEditorDndContext = ({ responseId }: Props) => {
  const {
    addRootCondition,
    addCondition,
    addEffect,
    createCondition,
    createEffect,
    getRootCondition,
    moveCondition,
    removeCondition,
    reorderCondition,
    reorderEffect,
    resolveAddableEffectBuildables,
    resolveConditionBuildables,
  } = useResponseDesigner();

  const effectBuildables = useMemo(() => resolveAddableEffectBuildables(), [resolveAddableEffectBuildables]);
  const conditionBuildables = useMemo(() => resolveConditionBuildables(), [resolveConditionBuildables]);

  const handleCreateCondition = ({ nodeId, typeId, parentId, index }: DragEndAddConditionEvent) => {
    const buildable = conditionBuildables.find((cb) => cb.name === typeId && cb.nodeId === nodeId);

    if (buildable) {
      const condition = createCondition(buildable);
      if (!condition) {
        console.warn(`Condition create failed for ${typeId}:${JSON.stringify(buildable)}`);
        return;
      }

      if (!parentId) {
        const groupBuildable = conditionBuildables.find((cb) => cb.name === "@@group");
        assert(groupBuildable);
        const groupCondition = createCondition(groupBuildable);
        assert(groupCondition);
        groupCondition.children.values = [condition];
        addRootCondition(groupCondition);
        return;
      }

      addCondition(condition, { parentId, name: GROUP_CONDITION_CHILDREN_KEY });

      if (index || index === 0) {
        // Add miniscule delay to ensure the condition is added to the state before reordering.
        setTimeout(() => {
          reorderCondition(condition.id, index);
        }, 5);
      }
    }
  };

  const handleCreateEffect = ({ nodeId, typeId, index }: DragEndAddEffectEvent) => {
    const buildable = effectBuildables.find((eb) => eb.name === typeId && eb.nodeId === nodeId);

    if (buildable) {
      const effect = createEffect(buildable);

      if (!effect) {
        console.warn(`Effect create failed for ${typeId}:${JSON.stringify(buildable)}`);
        return;
      }

      addEffect(effect);

      if (index || index === 0) {
        // Add miniscule delay to ensure the effect is added to the state before reordering.
        setTimeout(() => {
          reorderEffect(effect.id, index);
        }, 5);
      }
    }
  };

  const rootCondition = useMemo(() => getRootCondition(), [getRootCondition]);

  const onDragEnd = useOnDragEnd();

  const handleDragEnd: DragDropEventHandlers["onDragEnd"] = (dragEndParams, manager) => {
    if (dragEndParams.canceled) return;

    const dragEndEventResult = onDragEnd(dragEndParams, manager);
    if (!dragEndEventResult) return;

    switch (dragEndEventResult.eventType) {
      case "addCondition": {
        handleCreateCondition(dragEndEventResult);
        break;
      }
      case "addEffect": {
        handleCreateEffect(dragEndEventResult);
        break;
      }
      case "moveCondition": {
        const { id, parentId, index } = dragEndEventResult;
        moveCondition(id, { parentId, name: GROUP_CONDITION_CHILDREN_KEY }, index);
        break;
      }
      case "reorderCondition": {
        const { id, index } = dragEndEventResult;
        reorderCondition(id, index);
        break;
      }
      case "reorderEffect": {
        const { id, index } = dragEndEventResult;
        reorderEffect(id, index);
        break;
      }
    }
  };

  useEffect(() => {
    // @ts-ignore
    if (rootCondition && rootCondition.children.values?.length === 0) {
      removeCondition(rootCondition.id);
    }
  }, [removeCondition, rootCondition]);

  return (
    <DragDropProvider sensors={dndSensors} modifiers={[SnapVerticalCenterToCursor]} onDragEnd={handleDragEnd}>
      <ResponseEditorContextProvider rootCondition={rootCondition}>
        <ResponseEditor responseId={responseId} />
      </ResponseEditorContextProvider>
      {createPortal(
        <DragOverlay
          dropAnimation={({ source }) => {
            const parsed = draggableDataSchema.safeParse(source.data);
            if (parsed.success && parsed.data.rule.type === TYPE_BUILDABLE) return;
          }}
        >
          {(source) => source && <DragOverlayDragItem />}
        </DragOverlay>,
        document.body,
      )}
    </DragDropProvider>
  );
};
